import axios, { isAxiosError } from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { resolveApiBaseUrl } from './apiBaseUrl';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Tentativi di rete già fatti su questa richiesta. */
    retryCount?: number;
    /** La richiesta di refresh stessa: un suo 401 non avvia un altro refresh. */
    _skipAuthRefresh?: boolean;
    /** Già ripetuta dopo un refresh: un secondo 401 porta al logout. */
    _retryAfterRefresh?: boolean;
  }
}

const API_BASE_URL = resolveApiBaseUrl({
  env: import.meta.env,
  locationHostname: typeof window !== 'undefined' ? window.location.hostname : '',
});
const DEFAULT_TIMEOUT = 30000; // 30 secondi per operazioni standard
const UPLOAD_TIMEOUT = 60000; // 60 secondi per upload
const IMPORT_TIMEOUT = 180000; // Aumentato a 180 secondi (3 minuti) per operazioni di importazione
const BULK_IMPORT_TIMEOUT = 300000; // 5 minuti per importazioni massive
const MAX_RETRIES = 5; // Aumentato da 3 a 5 tentativi

const IS_DEV = Boolean(import.meta.env.DEV);
/** Imposta `VITE_DEBUG_API=true` in .env per log dettagliati sulle richieste (solo dev) */
const DEBUG_API = IS_DEV && import.meta.env.VITE_DEBUG_API === 'true';

/**
 * Ritardo artificiale sulle risposte "assignments" (solo se > 0).
 * In produzione è sempre 0. In dev, opzionale: `VITE_ASSIGNMENT_RESPONSE_DELAY_MS=300`
 */
const ASSIGNMENT_RESPONSE_DELAY_MS = (() => {
  if (!IS_DEV) return 0;
  const raw = import.meta.env.VITE_ASSIGNMENT_RESPONSE_DELAY_MS;
  if (raw === undefined || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.min(n, 5000) : 0;
})();

const RETRIABLE_METHODS = ['get', 'head', 'options'];

// axios mette il metodo in minuscolo prima degli interceptor.
const isRetriableMethod = (method: string | undefined) =>
  method !== undefined && RETRIABLE_METHODS.includes(method);

/** Il rifiuto di ogni chiamata: sempre un testo leggibile in `message`. */
export type ApiError = { message: string };

/** Lo stato HTTP di una chiamata rifiutata, se il server ha risposto. */
export const responseStatus = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

/** Il corpo di una chiamata rifiutata, se il server ha risposto: lo tipa chi lo legge. */
export const responseBody = (error: unknown): unknown =>
  isAxiosError(error) ? error.response?.data : undefined;

/** Il codice stabile di un rifiuto (`code` nel corpo), se il server l'ha dato. */
export const responseCode = (error: unknown): string | undefined => {
  const body = responseBody(error);
  return body && typeof body === 'object' && 'code' in body && typeof body.code === 'string'
    ? body.code
    : undefined;
};

/** Il corpo d'errore del backend: testo umano in `message`, a volte solo in `error`. */
type ErrorBody = {
  message?: string;
  error?: string;
  code?: string;
  errors?: Record<string, unknown>;
};

/**
 * Estrae il messaggio d'errore leggibile dalla risposta del backend, dando
 * priorità a `message` rispetto a `error`. Il global error handler del backend
 * mette il testo umano in `message` (`error` diventa `"fail"`/`"error"` per gli
 * AppError), mentre i suoi casi speciali (ValidationError / dup-key /
 * tenant-context) valorizzano solo `error`: la fallback su `error` li copre.
 */
const resolveResponseErrorMessage = (data: ErrorBody | undefined, fallbackMessage: string) =>
  data?.message || data?.error || fallbackMessage || 'Si è verificato un errore';

/**
 * Indica se un 422 trasporta una mappa di errori di validazione (`data.errors`).
 * Solo in quel caso l'interceptor sintetizza la forma `{ validationErrors }`;
 * altrimenti i 422 con payload strutturato (es. `code`/`blockingErrors`) devono
 * propagarsi mantenendo `error.response` intatto.
 */
const hasValidationErrors = (data: ErrorBody | undefined) => {
  const errors = data?.errors;
  return Boolean(errors && typeof errors === 'object' && Object.keys(errors).length > 0);
};

// Create axios instance with enhanced config
const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // Cookie refresh token (httpOnly) su /api/auth
});

let refreshInFlight: Promise<string> | null = null;

const refreshAccessToken = async () => {
  if (!refreshInFlight) {
    refreshInFlight = instance
      .post<{ token?: string }>(
        '/auth/refresh',
        {},
        { withCredentials: true, _skipAuthRefresh: true }
      )
      .then((res) => {
        const nextToken = res?.data?.token;
        if (!nextToken) {
          throw new Error('Refresh senza access token');
        }
        localStorage.setItem('token', nextToken);
        return nextToken;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

// Fixed request interceptor - removed problematic cache-control headers
instance.interceptors.request.use((config) => {
  try {
    // Get fresh token for every request
    const token = localStorage.getItem('token');
    if (DEBUG_API) {
      console.log(
        '[api] request',
        config.method?.toUpperCase(),
        config.url,
        'token:',
        Boolean(token)
      );
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (DEBUG_API) {
      console.warn('[api] no token for', config.url);
    }

    const rawUrl = typeof config.url === 'string' ? config.url : '';
    const isAbsolute = rawUrl.startsWith('http://') || rawUrl.startsWith('https://');

    // Ensure API prefix is added if not already there
    if (!isAbsolute && rawUrl && !rawUrl.startsWith('/api')) {
      if (rawUrl.startsWith('/')) {
        config.url = `/api${rawUrl}`;
      } else {
        config.url = `/api/${rawUrl}`;
      }
      if (DEBUG_API) {
        console.log('[api] prefixed url', config.url);
      }
    }

    // Su un retry il timeout resta quello alzato dall'interceptor di risposta
    if (!config.retryCount) {
      const url = config.url;
      // Un chiamante JS può ancora non passare l'url: la richiesta fallisce qui.
      if (url === undefined) {
        throw new TypeError('Richiesta senza url');
      }

      // Un invio di file (ogni corpo multipart) ha il tempo degli upload.
      if (config.data instanceof FormData || url.includes('upload') || url.includes('documents')) {
        config.timeout = UPLOAD_TIMEOUT;
      }

      // Timeout speciale per operazioni di importazione o massa
      if (url.includes('bulk-import')) {
        config.timeout = BULK_IMPORT_TIMEOUT;

        // Se sono presenti molti dati, disabilitiamo trasformazione JSON automatica
        // per migliorare le performance
        if (
          (config.data && typeof config.data === 'string' && config.data.length > 500000) ||
          (typeof config.data === 'object' && JSON.stringify(config.data).length > 500000)
        ) {
          config.maxContentLength = 50 * 1024 * 1024; // 50MB max
          config.maxBodyLength = 50 * 1024 * 1024; // 50MB max
        }
      } else if (
        url.includes('import') ||
        (url.includes('transactions') && (config.method === 'post' || config.method === 'put'))
      ) {
        config.timeout = IMPORT_TIMEOUT;
      }
    }

    // Track retry attempts
    config.retryCount = config.retryCount || 0;

    return config;
  } catch (error) {
    return Promise.reject(error);
  }
});

// Enhanced response interceptor
instance.interceptors.response.use(
  async (response) => {
    // Normalize response format
    const isBlobResponse = typeof Blob !== 'undefined' && response.data instanceof Blob;
    const isArrayBufferResponse =
      typeof ArrayBuffer !== 'undefined' && response.data instanceof ArrayBuffer;
    if (
      !isBlobResponse &&
      !isArrayBufferResponse &&
      response.data &&
      response.data.success === undefined
    ) {
      response.data.success = response.status >= 200 && response.status < 300;
    }

    if (
      ASSIGNMENT_RESPONSE_DELAY_MS > 0 &&
      response.config.url?.includes('assignments') &&
      (response.config.method === 'post' ||
        response.config.method === 'put' ||
        response.config.method === 'delete')
    ) {
      await new Promise((resolve) => setTimeout(resolve, ASSIGNMENT_RESPONSE_DELAY_MS));
    }

    return response;
  },
  // Arriva qui anche un errore lanciato dall'interceptor di richiesta, che non è un
  // AxiosError: `config`, `request` e `response` mancano, come AxiosError ammette.
  async (error: AxiosError<ErrorBody>) => {
    const originalRequest = error.config;

    if (axios.isCancel(error)) {
      error.message = 'Richiesta annullata';
      return Promise.reject(error);
    }

    // Problemi di connessione: si ritentano, con backoff esponenziale, solo le
    // letture. Una scrittura senza risposta può essere già arrivata al server, e
    // ripeterla creerebbe un doppione (#558).
    if (
      isRetriableMethod(originalRequest?.method) &&
      (error.code === 'ECONNABORTED' ||
        error.message.includes('timeout') ||
        error.message.includes('Network Error') ||
        (error.request && !error.response)) &&
      originalRequest?.retryCount !== undefined &&
      originalRequest.retryCount < MAX_RETRIES
    ) {
      originalRequest.retryCount += 1;

      // Calcola un delay esponenziale con jitter per evitare richieste simultanee
      const baseDelay = Math.pow(2, originalRequest.retryCount) * 1000;
      const jitter = Math.random() * 1000; // Aggiunge fino a 1 secondo di randomizzazione
      const delay = baseDelay + jitter;

      await new Promise((resolve) => setTimeout(resolve, delay));

      return instance(originalRequest);
    }

    if (error.response) {
      const request = error.response.config;
      error.message = resolveResponseErrorMessage(error.response.data, error.message);

      // Handle authentication errors (401)
      if (error.response.status === 401) {
        const isAuthEndpoint =
          request.url?.includes('/auth/login') ||
          request.url?.includes('/auth/refresh') ||
          request.url?.includes('/auth/logout');

        const canRetryRefresh =
          !request._skipAuthRefresh && !request._retryAfterRefresh && !isAuthEndpoint;

        if (canRetryRefresh) {
          try {
            const nextToken = await refreshAccessToken();
            request._retryAfterRefresh = true;
            request.headers.Authorization = `Bearer ${nextToken}`;
            return instance(request);
          } catch {
            /* fall through to logout */
          }
        }

        const isBackgroundTask =
          request.url?.includes('/dashboard') ||
          request.url?.includes('/notifications') ||
          request.url?.includes('/maintenance');

        if (window.location.pathname !== '/login' && !isBackgroundTask) {
          localStorage.removeItem('token');
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
          return Promise.reject(new Error('Sessione scaduta, effettua nuovamente il login'));
        }

        return Promise.reject(error);
      }

      if (error.response.status === 400) {
        const code = error.response.data?.code;
        const errText = resolveResponseErrorMessage(error.response.data, '');
        if (code === 'SELECT_TENANT' || errText.toLowerCase().includes('select tenant')) {
          return Promise.reject(error);
        }
      }

      if (error.response.status === 403) {
        const code = error.response.data?.code;
        const errText = resolveResponseErrorMessage(error.response.data, '');
        if (
          code === 'TENANT_NOT_ASSOCIATED' ||
          code === 'TENANT_FORBIDDEN' ||
          errText.toLowerCase().includes('tenant')
        ) {
          return Promise.reject(error);
        }
      }

      // Handle validation errors (422)
      if (error.response.status === 422) {
        // Sintetizza la forma { validationErrors } SOLO quando il 422 porta una
        // mappa di errori di campo. I 422 con payload strutturato (es.
        // code:'BLOCKING_ERRORS', blockingErrors, preview) non hanno data.errors:
        // devono propagarsi mantenendo error.response intatto per i chiamanti.
        if (hasValidationErrors(error.response.data)) {
          return Promise.reject({
            message: 'Errori di validazione',
            validationErrors: error.response.data?.errors,
          });
        }
        // Altrimenti: fall-through alla gestione generica sotto (preserva error.response).
      }

      // Handle 404 errors with specific messages
      if (error.response.status === 404) {
        // Skip redirects for 404s - they might be API endpoints that don't exist
        const urlParts = String(request.url).split('/');
        const entityType = urlParts[2]; // e.g., 'drivers' from '/api/drivers/123'

        let errorMessage;

        if (entityType && urlParts.length > 3) {
          // Resource-specific error messages
          switch (entityType) {
            case 'drivers':
              errorMessage = 'Conducente non trovato';
              break;
            case 'vehicles':
              errorMessage = 'Veicolo non trovato';
              break;
            case 'fines':
              errorMessage = 'Multa non trovata';
              break;
            case 'fuel-cards':
            case 'fuelCards':
              errorMessage = 'Carta carburante non trovata';
              break;
            case 'auth':
              errorMessage = 'Endpoint di autenticazione non trovato';
              break;
            default:
              errorMessage = 'Risorsa non trovata';
          }
        } else {
          errorMessage = 'Endpoint non trovato';
        }

        error.message = errorMessage;
        return Promise.reject(error);
      }

      return Promise.reject(error);
    } else if (error.request) {
      // Request made but no response received

      let errorMessage = 'Nessuna risposta dal server. ';

      if (error.code === 'ECONNREFUSED') {
        errorMessage += 'Il server non è raggiungibile. Verifica che il backend sia in esecuzione.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage += 'La connessione è scaduta. Il server potrebbe essere sovraccarico.';
      } else {
        errorMessage += 'Verifica la tua connessione di rete.';
      }

      error.message = errorMessage;
      return Promise.reject(error);
    } else {
      error.message = 'Errore durante la configurazione della richiesta';
      return Promise.reject(error);
    }
  }
);

// Create a dedicated file operations client that bypasses the problematic Vite proxy
const createFileClient = () => {
  // Use same environment detection as getBaseUrl
  const isViteDevProxy =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
    !API_BASE_URL;

  const fileClient = axios.create({
    baseURL: isViteDevProxy ? '' : API_BASE_URL,
    timeout: UPLOAD_TIMEOUT,
    validateStatus: (status) => status < 500,
  });

  // Add authentication interceptor
  fileClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const rawUrl = typeof config.url === 'string' ? config.url : '';
    const isAbsolute = rawUrl.startsWith('http://') || rawUrl.startsWith('https://');
    if (!isAbsolute && rawUrl && !rawUrl.startsWith('/api')) {
      if (rawUrl.startsWith('/')) {
        config.url = `/api${rawUrl}`;
      } else {
        config.url = `/api/${rawUrl}`;
      }
    }
    return config;
  });

  // Add response interceptor for consistent error handling
  fileClient.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return fileClient;
};

const fileClient = createFileClient();

/**
 * Il client HTTP dell'app: aggiunge token e prefisso `/api`, ritenta gli errori di
 * rete, rinnova il token su un 401 e mette un testo leggibile in `error.message`.
 * Il tipo della risposta lo sceglie il chiamante (`apiClient.get<Body>(…)`).
 */
const apiClient = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) => instance.get<T>(url, config),
  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    instance.post<T>(url, data, config),
  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    instance.put<T>(url, data, config),
  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    instance.delete<T>(url, config),
  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    instance.patch<T>(url, data, config),

  // Use dedicated file client for reliable multipart upload
  uploadFormData: async <T = unknown>(
    url: string,
    formData: FormData,
    config: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> =>
    fileClient.post<T>(url, formData, {
      ...config,
      headers: {
        // Let browser set Content-Type automatically for multipart
        ...config.headers,
      },
    }),

  getFile: async <T = unknown>(
    url: string,
    config: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> => fileClient.get<T>(url, config),
};

export default apiClient;
